import { makeStyles } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import type {
  HTMLProps,
  ReactNode
} from 'react';
import React, {
  forwardRef, useCallback, useEffect
} from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { THEMES } from 'src/constants';
import type { Theme } from 'src/theme';
import track from 'src/utils/analytics';
import NetworkHandler from './NetworkHandler';
interface PageProps extends HTMLProps<HTMLDivElement> {
  children?: ReactNode;
  title?: string;
  storeBgLogo?: boolean;
  exoBgLogo?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  backgroundStoreLogo: {
    backgroundImage: 'url(/static/atlantaHawksTransparent.png)',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
    backgroundPosition: 'right 10px bottom 10px',
    backgroundSize: '300px',
  },
  backgroundExoLogo: {
    backgroundImage: theme.name === THEMES.DARK_MODE
      ? 'url(/static/logo-white.svg)'
      : 'url(/static/logo.svg)',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
    backgroundPosition: 'right 10px bottom 10px',
    backgroundSize: '200px',
  }
}));

const Page = forwardRef<HTMLDivElement, PageProps>(({
  children,
  title = '',
  className,
  storeBgLogo = false,
  exoBgLogo = false,
  ...rest
}, ref) => {
  const classes = useStyles();
  const location = useLocation();
  const mainTitle = 'EXO Administration';
  const theme: Theme = useTheme();
  const sendPageViewEvent = useCallback(() => {
    track.pageview({
      page_path: location.pathname
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    sendPageViewEvent();
  }, [sendPageViewEvent]);

  let classesForPage = className;
  if (storeBgLogo) {
    classesForPage = classNames(classes.backgroundStoreLogo, className);
  } else if (exoBgLogo) {
    classesForPage = classNames(classes.backgroundExoLogo, className);
  }

  return (
    <div
      ref={ref as any}
      {...rest}
      style={{ position: 'relative' }}
      className={classesForPage}
    >
      <Helmet>
        <title>{title ? `${title} - ${mainTitle}` : mainTitle}</title>
        <meta name="theme-color" content={theme.customs.theme} />
      </Helmet>
      {children}
      <NetworkHandler/>
    </div>
  );
});

Page.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string
};

export default Page;
